/**
 * Terms of Service Page
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicLayout from '../../components/layout/PublicLayout';

export default function TermsOfServicePage() {
  const { t } = useTranslation('legal');
  const lastUpdated = '2024-12-07';

  return (
    <PublicLayout bgClass="bg-gray-50">
      <div className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('terms.title')}</h1>
          <p className="text-sm text-gray-500 mb-8">{t('terms.lastUpdated')}: {lastUpdated}</p>

          <div className="prose prose-blue max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('terms.section1.title')}</h2>
              <p className="text-gray-600 mb-4">{t('terms.section1.p1')}</p>
              <p className="text-gray-600">{t('terms.section1.p2')}</p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('terms.section2.title')}</h2>
              <p className="text-gray-600 mb-4">{t('terms.section2.p1')}</p>
              <ul className="list-disc ltr:pl-6 rtl:pr-6 text-gray-600 space-y-2">
                {(t('terms.section2.list', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('terms.section3.title')}</h2>
              <p className="text-gray-600 mb-4">{t('terms.section3.p1')}</p>
              <ul className="list-disc ltr:pl-6 rtl:pr-6 text-gray-600 space-y-2">
                {(t('terms.section3.list', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <p className="text-gray-600 mt-4">{t('terms.section3.p2')}</p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('terms.section4.title')}</h2>
              <p className="text-gray-600 mb-4">{t('terms.section4.p1')}</p>
              <ul className="list-disc ltr:pl-6 rtl:pr-6 text-gray-600 space-y-2">
                {(t('terms.section4.list', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('terms.section5.title')}</h2>
              <p className="text-gray-600 mb-4">
                <strong>{t('terms.section5.yourContent')}</strong> {t('terms.section5.yourContentText')}
              </p>
              <p className="text-gray-600 mb-4">
                <strong>{t('terms.section5.respondentData')}</strong> {t('terms.section5.respondentDataText')}
              </p>
              <p className="text-gray-600">
                <strong>{t('terms.section5.dataSecurity')}</strong> {t('terms.section5.dataSecurityText')}{' '}
                <Link to="/privacy" className="text-blue-600 hover:underline">{t('terms.section5.privacyPolicy')}</Link>{' '}
                {t('terms.section5.forDetails')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('terms.section6.title')}</h2>
              <p className="text-gray-600 mb-4">{t('terms.section6.p1')}</p>
              <p className="text-gray-600">{t('terms.section6.p2')}</p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('terms.section7.title')}</h2>
              <p className="text-gray-600 mb-4">{t('terms.section7.p1')}</p>
              <ul className="list-disc ltr:pl-6 rtl:pr-6 text-gray-600 space-y-2">
                {(t('terms.section7.list', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('terms.section8.title')}</h2>
              <p className="text-gray-600 mb-4">{t('terms.section8.p1')}</p>
              <p className="text-gray-600">{t('terms.section8.p2')}</p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('terms.section9.title')}</h2>
              <p className="text-gray-600">{t('terms.section9.p1')}</p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('terms.section10.title')}</h2>
              <p className="text-gray-600 mb-4">{t('terms.section10.p1')}</p>
              <p className="text-gray-600">{t('terms.section10.p2')}</p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('terms.section11.title')}</h2>
              <p className="text-gray-600">{t('terms.section11.p1')}</p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('terms.section12.title')}</h2>
              <p className="text-gray-600">
                {t('terms.section12.p1')}{' '}
                <a href="mailto:legal@example.com" className="text-blue-600 hover:underline">
                  legal@example.com
                </a>
              </p>
            </section>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <Link to="/" className="text-blue-600 hover:underline">
              &larr; {t('terms.backToHome')}
            </Link>
          </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
